import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Admins from './pages/Admins';
import Students from './pages/Students';
import StudentsList from './pages/StudentsList';
import HowToApply from './pages/HowToApply';
import WhatWeOffer from './pages/WhatWeOffer';
import CompanyHowToApply from './pages/CompanyHowToApply';
import CompanyWhatWeOffer from './pages/CompanyWhatWeOffer';
import OurClients from './pages/OurClients';
import Companies from './pages/Companies';
import Transactions from './pages/Transactions';
import Events from './pages/Events';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Interests from './pages/Interests';
import Nationalities from './pages/Nationalities';
import FAQs from './pages/FAQs';
import About from './pages/About';
import WhatWeOfferAbout from './pages/WhatWeOfferAbout';
import Settings from './pages/Settings';
import HomeBanners from './pages/HomeBanners';
import WhatWeAre from './pages/WhatWeAre';
import Subscriptions from './pages/Subscriptions';
import EventRequests from './pages/EventRequests';
import UserSubscriptions from './pages/UserSubscriptions';
import ContactUs from './pages/ContactUs';
import SystemImages from './pages/SystemImages';
import { tokenManager } from './utils/api';
// import './App.css';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already authenticated
    const checkAuth = () => {
      const isAuth = tokenManager.isAuthenticated();
      const token = tokenManager.getToken();
      const rawToken = localStorage.getItem('authToken');
      
      setIsAuthenticated(isAuth);
      
      if (isAuth) {
        // Get user data from localStorage or make API call
        const userData = localStorage.getItem('userData');
        if (userData) {
          setUser(JSON.parse(userData));
        } else {
          // Set default user data for demo
          setUser({
            id: 1,
            name: 'Admin User',
            email: 'admin@lynx.com',
            role: 'Administrator'
          });
        }
      }
      
      setLoading(false);
    };

    checkAuth();

    // Also check auth periodically to handle cases where token is set after component mount
    const authInterval = setInterval(() => {
      const currentAuth = tokenManager.isAuthenticated();
      if (currentAuth !== isAuthenticated) {
        console.log('Auth state changed, updating...', currentAuth);
        checkAuth();
      }
    }, 1000);

    return () => clearInterval(authInterval);
  }, [isAuthenticated]);

  const handleLoginSuccess = (userData) => {
    setIsAuthenticated(true);
    setUser(userData);
    localStorage.setItem('userData', JSON.stringify(userData));
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setUser(null);
    tokenManager.removeToken();
    localStorage.removeItem('userData');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          <span className="text-gray-600">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <div className="App">
          <Routes>
            <Route 
              path="/login" 
              element={
                isAuthenticated ? 
                  <Navigate to="/dashboard" replace /> : 
                  <Login onLoginSuccess={handleLoginSuccess} />
              } 
            />
            <Route 
              path="/dashboard" 
              element={
                isAuthenticated ? 
                  <Dashboard user={user} onLogout={handleLogout} /> : 
                  <Navigate to="/login" replace />
              } 
            />
            <Route 
              path="/dashboard/home/home-banners" 
              element={
                isAuthenticated ? 
                  <HomeBanners user={user} onLogout={handleLogout} /> : 
                  <Navigate to="/login" replace />
              } 
            />
            <Route 
              path="/dashboard/home/what-we-are" 
              element={
                isAuthenticated ? 
                  <WhatWeAre user={user} onLogout={handleLogout} /> : 
                  <Navigate to="/login" replace />
              } 
            />
            <Route 
              path="/dashboard/admins" 
              element={
                isAuthenticated ? 
                  <Admins user={user} onLogout={handleLogout} /> : 
                  <Navigate to="/login" replace />
              } 
            />
            <Route 
              path="/dashboard/students" 
              element={
                isAuthenticated ? 
                  <Students user={user} onLogout={handleLogout} /> : 
                  <Navigate to="/login" replace />
              } 
            />
            <Route 
              path="/dashboard/students/how-to-apply" 
              element={
                isAuthenticated ? 
                  <HowToApply user={user} onLogout={handleLogout} /> : 
                  <Navigate to="/login" replace />
              } 
            />
            <Route 
              path="/dashboard/students/what-we-offer" 
              element={
                isAuthenticated ? 
                  <WhatWeOffer user={user} onLogout={handleLogout} /> : 
                  <Navigate to="/login" replace />
              } 
            />
            <Route 
              path="/dashboard/students-list" 
              element={
                isAuthenticated ? 
                  <StudentsList user={user} onLogout={handleLogout} /> : 
                  <Navigate to="/login" replace />
              } 
            />
            <Route 
              path="/dashboard/interests" 
              element={
                isAuthenticated ? 
                  <Interests user={user} onLogout={handleLogout} /> : 
                  <Navigate to="/login" replace />
              } 
            />
            <Route 
              path="/dashboard/nationalities" 
              element={
                isAuthenticated ? 
                  <Nationalities user={user} onLogout={handleLogout} /> : 
                  <Navigate to="/login" replace />
              } 
            />
            <Route 
              path="/dashboard/company/how-to-apply" 
              element={
                isAuthenticated ? 
                  <CompanyHowToApply user={user} onLogout={handleLogout} /> : 
                  <Navigate to="/login" replace />
              } 
            />
            <Route 
              path="/dashboard/company/what-we-offer" 
              element={
                isAuthenticated ? 
                  <CompanyWhatWeOffer user={user} onLogout={handleLogout} /> : 
                  <Navigate to="/login" replace />
              } 
            />
            <Route 
              path="/dashboard/our-clients" 
              element={
                isAuthenticated ? 
                  <OurClients user={user} onLogout={handleLogout} /> : 
                  <Navigate to="/login" replace />
              } 
            />
            <Route 
              path="/dashboard/companies" 
              element={
                isAuthenticated ? 
                  <Companies user={user} onLogout={handleLogout} /> : 
                  <Navigate to="/login" replace />
              } 
            />
            <Route 
              path="/dashboard/transactions" 
              element={
                isAuthenticated ? 
                  <Transactions user={user} onLogout={handleLogout} /> : 
                  <Navigate to="/login" replace />
              } 
            />
            <Route 
              path="/dashboard/events" 
              element={
                isAuthenticated ? 
                  <Events user={user} onLogout={handleLogout} /> : 
                  <Navigate to="/login" replace />
              } 
            />
            <Route 
              path="/dashboard/event-requests" 
              element={
                isAuthenticated ? 
                  <EventRequests user={user} onLogout={handleLogout} /> : 
                  <Navigate to="/login" replace />
              } 
            />
            <Route 
              path="/dashboard/cms-pages/terms" 
              element={
                isAuthenticated ? 
                  <Terms user={user} onLogout={handleLogout} /> : 
                  <Navigate to="/login" replace />
              } 
            />
            <Route 
              path="/dashboard/cms-pages/privacy" 
              element={
                isAuthenticated ? 
                  <Privacy user={user} onLogout={handleLogout} /> : 
                  <Navigate to="/login" replace />
              } 
            />
            <Route 
              path="/dashboard/cms-pages/faqs" 
              element={
                isAuthenticated ? 
                  <FAQs user={user} onLogout={handleLogout} /> : 
                  <Navigate to="/login" replace />
              } 
            />
            <Route 
              path="/dashboard/about/about" 
              element={
                isAuthenticated ? 
                  <About user={user} onLogout={handleLogout} /> : 
                  <Navigate to="/login" replace />
              } 
            />
            <Route 
              path="/dashboard/about/what-we-offer" 
              element={
                isAuthenticated ? 
                  <WhatWeOfferAbout user={user} onLogout={handleLogout} /> : 
                  <Navigate to="/login" replace />
              } 
            />
            <Route 
              path="/dashboard/subscriptions" 
              element={
                isAuthenticated ? 
                  <Subscriptions user={user} onLogout={handleLogout} /> : 
                  <Navigate to="/login" replace />
              } 
            />
            <Route 
              path="/dashboard/user-subscriptions" 
              element={
                isAuthenticated ? 
                  <UserSubscriptions user={user} onLogout={handleLogout} /> : 
                  <Navigate to="/login" replace />
              } 
            />
            <Route 
              path="/dashboard/contact-us" 
              element={
                isAuthenticated ? 
                  <ContactUs user={user} onLogout={handleLogout} /> : 
                  <Navigate to="/login" replace />
              } 
            />
            <Route 
              path="/dashboard/settings"
              element={
                isAuthenticated ? 
                  <Settings user={user} onLogout={handleLogout} /> : 
                  <Navigate to="/login" replace />
              } 
            />
            <Route 
              path="/dashboard/system-images" 
              element={
                isAuthenticated ? 
                  <SystemImages user={user} onLogout={handleLogout} /> : 
                  <Navigate to="/login" replace />
              } 
            />
            <Route 
              path="/" 
              element={
                <Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />
              } 
            />
            {/* Add more routes as needed */}
            <Route 
              path="*" 
              element={
                <Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />
              } 
            />
          </Routes>
        </div>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
